import { useMemo } from 'react';
import { InitiatePayload } from 'flows/core/types/cards';
import { isTaskCompletedOrValidatingOrPending } from 'flows/core/utils/card-makers/utils';
import { differenceInDays, parseISO, format, isValid } from 'date-fns';

export const useDaysLeft = (
  currentMoveDate: string | null | undefined
): number => {
  return useMemo(() => {
    if (!currentMoveDate) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [year, month, day] = currentMoveDate.split('-').map(Number);
    const targetDate = new Date(year, month - 1, day);

    return differenceInDays(targetDate, today);
  }, [currentMoveDate]);
};

export const getDaysText = (
  daysLeft: number,
  moveDate: string | null | undefined
): string => {
  let formattedDate = '';
  const parsed = moveDate ? parseISO(moveDate) : null;
  if (parsed && isValid(parsed)) {
    formattedDate = format(parsed, 'MMMM do');
  }
  if (daysLeft === 0) return "It's move-in day!";
  if (daysLeft === 1) return "Tomorrow's the big day!";
  if (daysLeft < 0) return 'Welcome home!';
  return formattedDate
    ? `Only ${daysLeft} days left until your new lease ${formattedDate}`
    : `Only ${daysLeft} days left until your new lease`;
};

/**
 * A card counts toward progress only if it is required of the resident or
 * flagged as a priority. Kept in one place because every progress surface has
 * to agree on it: when a copy of this predicate drifted, the renewal hero
 * started counting optional tasks in its denominator (UPO-3894).
 */
export const isProgressCountable = (card: InitiatePayload): boolean =>
  Boolean(card.data?.required || card.data?.priority);

/**
 * Completed cards that count toward progress — the numerator, and the part of
 * the denominator that isn't still outstanding.
 */
export const getCompletedCountableCards = (
  completedCards: InitiatePayload[] | undefined
): InitiatePayload[] =>
  (completedCards || []).filter(
    (card) =>
      isTaskCompletedOrValidatingOrPending(card.data) &&
      isProgressCountable(card)
  );

/**
 * Every number a progress bar needs. Components that render a bar take this
 * and derive nothing themselves, so two bars on the same move cannot disagree.
 */
export interface ProgressGate {
  /** Denominator: outstanding countable tasks plus finished ones. */
  totalTasks: number;
  /** Finished and counted. */
  completedTasks: number;
  /** Submitted but awaiting review — progress, but not finished. */
  validatingTasks: number;
  /** What a resident reads as "done": completed plus in-review. */
  displayedDone: number;
  /** Neither finished nor in review. Never counts in-review tasks twice. */
  remaining: number;
  /** Whole-bar fill, completed and in-review together. */
  pct: number;
  /** Fill of the completed segment alone. */
  completedPct: number;
  /** Fill of the in-review segment alone. */
  validatingPct: number;
  completedCountableCards: InitiatePayload[];
  showProgressBar: boolean;
}

const asPct = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

/**
 * The single implementation of progress arithmetic, and the single decision on
 * whether a bar renders at all.
 *
 * Pure rather than a hook so that callers already holding the cards can use it
 * without invoking `useEssentialsTabCards` a second time — that hook fires
 * tracking on mount, so calling it twice on one page double-counts events.
 * `useProgressBarData` wraps this for callers that need the fetch as well.
 */
export const getProgressGate = ({
  priorityCards,
  completedCards,
  validatingCards,
  isUPO,
  loading,
}: {
  priorityCards: InitiatePayload[] | undefined;
  completedCards: InitiatePayload[] | undefined;
  validatingCards?: InitiatePayload[] | undefined;
  isUPO: boolean;
  loading: boolean;
}): ProgressGate => {
  const completedCountableCards = getCompletedCountableCards(completedCards);
  const completedTasks = completedCountableCards.length;
  const validatingTasks = validatingCards?.length || 0;
  const totalTasks = (priorityCards?.length || 0) + completedTasks;
  const displayedDone = completedTasks + validatingTasks;

  return {
    totalTasks,
    completedTasks,
    validatingTasks,
    displayedDone,
    remaining: totalTasks - displayedDone,
    pct: asPct(displayedDone, totalTasks),
    completedPct: asPct(completedTasks, totalTasks),
    validatingPct: asPct(validatingTasks, totalTasks),
    completedCountableCards,
    // Nothing to count means nothing to show. Non-UPO moves have no priority
    // cards at all, so without the isUPO term a resident with one completed
    // required task would read 100% while their to-dos sat below the bar.
    //
    // The VBA chat header gates on this flag rather than guarding itself, so
    // the zero-task case hides its bar too. That is intended: gating at the
    // data layer keeps the chat's ready-state machine in step, since
    // useVBAUIState treats a false flag as "no animation to wait for".
    showProgressBar: isUPO && !loading && totalTasks > 0,
  };
};

export interface ProgressTotals {
  totalTasks: number;
  completedTasks: number;
  pct: number;
}

export const useProgressTotals = (
  priorityCards: InitiatePayload[] | undefined,
  completedCards: InitiatePayload[] | undefined
): ProgressTotals => {
  return useMemo(() => {
    const completedRequiredCount =
      getCompletedCountableCards(completedCards).length;

    const totalTasks = (priorityCards?.length || 0) + completedRequiredCount;
    const completedTasks =
      (priorityCards?.filter((card: InitiatePayload) =>
        isTaskCompletedOrValidatingOrPending(card.data)
      ).length || 0) + completedRequiredCount;

    const pct =
      totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return { totalTasks, completedTasks, pct };
  }, [priorityCards, completedCards]);
};
